import React, { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PaginatedEntityTable, ShareButton } from "components/common";
import { ButtonToolbar } from "components/bootstrap";
import { fetchPaginatedCollectionEntities, keyFn } from "collections/hooks/useCollectionEntities";
import EntityShareModal from "components/permissions/EntityShareModal";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { useTranslation } from "react-i18next";
import CollectionEntityActions from "./CollectionEntityActions";
import customColumnRenderers from "./customColumnRenderers";
import BulkActions from "./BulkActions";
export const DEFAULT_LAYOUT = {
  entityTableId: "collection_entities",
  defaultPageSize: 20,
  defaultSort: { attributeId: "name", direction: "asc" },
  defaultDisplayedAttributes: ["name", "type", "owners"],
  defaultColumnOrder: ["name", "type", "owners"]
};
const CollectionEntitiesList = ({ collectionId, collectionTitle }) => {
  const { t } = useTranslation("enterprise_collections");
  const queryClient = useQueryClient();
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState(false);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const onBulkSelect = (selected, data) => {
    setSelectedEntities(data.filter((entity) => selected.includes(entity.id)));
  };
  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: onBulkSelect,
      actions: /* @__PURE__ */ React.createElement(BulkActions, { selectedEntities, collectionId })
    }),
    [collectionId, selectedEntities]
  );
  const refetchCollection = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY, collectionId]
    }),
    [queryClient, collectionId]
  );
  const handleShareClose = () => {
    refetchCollection();
    toggleCollectionContentShareModal();
  };
  const collectionEntitiesActions = useCallback(
    ({ id, name, type }) => /* @__PURE__ */ React.createElement(CollectionEntityActions, { type, collectionId, id, name }),
    [collectionId]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: t("shared.collection_content"),
      entityActions: collectionEntitiesActions,
      tableLayout: DEFAULT_LAYOUT,
      fetchEntities: (searchParams) => fetchPaginatedCollectionEntities(collectionId, searchParams),
      keyFn: (searchParams) => keyFn(collectionId, searchParams),
      columnRenderers: customColumnRenderers,
      entityAttributesAreCamelCase: false,
      searchPlaceholder: t("entities_list.search_placeholder"),
      bulkSelection,
      topRightCol: /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
        ShareButton,
        {
          title: t("shared.share_content"),
          entityId: collectionId,
          entityType: "collection_entities",
          onClick: toggleCollectionContentShareModal
        }
      ))
    }
  ), showCollectionContentShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: collectionId,
      entityType: "collection_entities",
      entityTypeTitle: t("shared.collection_content"),
      entityTitle: collectionTitle,
      description: t("entities_list.share_description"),
      onClose: handleShareClose
    }
  ));
};
export default CollectionEntitiesList;
